import type { MotorSumInsuredRange } from "@/D2C/features/car-quote/utils/motorSumInsured";
import {
  formatCurrencyValue,
  normalizeCurrencyInput,
} from "@/D2C/features/car-quote/utils/motorSumInsured";
import { PHONE_LENGTHS } from "@/shared/config/contact";
import { LOCALISATION_CONFIG } from "@/shared/config/localisation";
import { isValidIndianVehicleNumber } from "@/shared/utils/indianVehicleNumber";
import {
  getNationalIdBirthYear,
  getNationalIdLabel,
  getNationalIdValidationMessage,
  isValidNationalId,
} from "@/shared/utils/nationalId";
import { z } from "zod";
import { emailSchema } from "./emailSchema";
import { createPersonNameSchema } from "./nameSchemas";

const isValidDateString = (value: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }

  const date = new Date(`${value}T00:00:00`);
  return !Number.isNaN(date.getTime());
};

const isFutureDateString = (value: string) => {
  const date = new Date(`${value}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date > today;
};

const createCarValueSchema = (range: MotorSumInsuredRange | null) =>
  z
    .string()
    .min(1, "Car value is required")
    .refine((value) => /^\d+$/.test(normalizeCurrencyInput(value)), "Enter a valid car value")
    .refine(
      (value) => {
        const normalizedValue = normalizeCurrencyInput(value);

        if (!normalizedValue) {
          return false;
        }

        const numericValue = Number(normalizedValue);

        if (!range) {
          return numericValue > 0;
        }

        return numericValue >= range.min && numericValue <= range.max;
      },
      range
        ? `Car value must be between ${LOCALISATION_CONFIG.currencyCode} ${formatCurrencyValue(range.min)} and ${LOCALISATION_CONFIG.currencyCode} ${formatCurrencyValue(range.max)}`
        : "Enter a valid car value"
    );

export const createVehicleDetailsSchema = (range: MotorSumInsuredRange | null = null) =>
  z
    .object({
      carModel: z.string().min(1, "Car model is required"),
      modelYear: z.string().min(1, "Model year is required"),
      vehicleVariant: z.string().min(1, "Vehicle variant is required"),
      gccSpec: z.boolean(),
      isNewCar: z.boolean(),
      registeredEmirate: z.string().min(1, "Registered emirate is required"),
      registrationDate: z
        .string()
        .min(1, "Registration date is required")
        .refine(isValidDateString, "Enter a valid registration date"),
      registeredUnder: z.enum(["Individual", "Company"]),
      carValue: createCarValueSchema(range),
    })
    .superRefine((data, ctx) => {
      if (!isValidDateString(data.registrationDate)) {
        return;
      }

      const registrationDate = new Date(`${data.registrationDate}T00:00:00`);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (data.isNewCar) {
        const maxNewCarDate = new Date(today);
        maxNewCarDate.setDate(maxNewCarDate.getDate() + 30);

        if (registrationDate < today || registrationDate > maxNewCarDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "For new car, registration date must be between today and next 30 days",
            path: ["registrationDate"],
          });
        }

        return;
      }

      if (!/^\d{4}$/.test(data.modelYear)) {
        return;
      }

      const minUsedCarDate = new Date(`${data.modelYear}-01-01T00:00:00`);
      if (registrationDate < minUsedCarDate || registrationDate > today) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "For used car, registration date must be between model year and today",
          path: ["registrationDate"],
        });
      }
    });

export const vehicleDetailsSchema = createVehicleDetailsSchema();

export const createPersonalDetailsSchema = (
  options: { requireVehicleRegistrationNumber?: boolean } = {}
) => {
  const { requireVehicleRegistrationNumber = false } = options;

  return z.object({
    firstName: createPersonNameSchema("First name"),
    lastName: createPersonNameSchema("Last name"),
    mobileNumber: z
      .object({
        countryCode: z.string().min(1, "Country code is required"),
        number: z
          .string()
          .min(1, "Enter a valid mobile number")
          .regex(/^\d+$/, "Please enter only digits. Eg. 501234567"),
      })
      .superRefine((value, ctx) => {
        const requiredLength = PHONE_LENGTHS[value.countryCode];

        if (requiredLength && value.number.length !== requiredLength) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Enter a valid ${requiredLength}-digit mobile number`,
            path: ["number"],
          });
        }

        if (value.countryCode === "971") {
          const allowedPrefixes = ["50", "52", "54", "55", "56", "58"];

          if (value.number.length !== 9) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Please enter only 9 digits Eg. 501234567.",
              path: ["number"],
            });
            return;
          }

          if (!allowedPrefixes.some((prefix) => value.number.startsWith(prefix))) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "UAE mobile must start with 50, 52, 54, 55, 56, or 58.",
              path: ["number"],
            });
          }

          return;
        }
      }),
    email: emailSchema,
    partnerCode: z
      .string()
      .trim()
      .optional()
      .refine((value) => !value || /^[A-Za-z0-9]{8}$/.test(value), {
        message: "Partner Code must be 8 alphanumeric characters",
      }),
    friendsAndFamilyContact: z
      .string()
      .trim()
      .optional()
      .refine((value) => !value || /^\d{9}$/.test(value), {
        message: "Friends & Family Contact must be a 9 digit number",
      }),
    marketingConsent: z.enum(["yes", "no"]),
    vehicleRegistrationNumber: requireVehicleRegistrationNumber
      ? z
          .string()
          .trim()
          .min(1, "Vehicle registration number is required")
          .refine(
            (value) => isValidIndianVehicleNumber(value),
            "Enter a valid Indian vehicle number"
          )
      : z
          .string()
          .trim()
          .optional()
          .refine(
            (value) => !value || isValidIndianVehicleNumber(value),
            "Enter a valid Indian vehicle number"
          ),
  });
};

export const personalDetailsSchema = createPersonalDetailsSchema();

export const driverDetailsSchema = z
  .object({
    dateOfBirth: z
      .string()
      .min(1, "Date of birth is required")
      .refine(isValidDateString, "Enter a valid date of birth")
      .refine((value) => !isFutureDateString(value), "Date of birth cannot be in the future")
      .refine((value) => {
        const birthDate = new Date(value);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }

        return age >= 18;
      }, "Date of birth must be at least 18 years old")
      .refine((value) => {
        const birthDate = new Date(value);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }

        return age <= 90;
      }, "Date of birth cannot be more than 90 years ago"),
    nationality: z.string().min(1, "Nationality is required"),
    gender: z
      .string()
      .min(1, "Please select gender")
      .refine((value) => ["male", "female"].includes(value), {
        message: "Invalid gender",
      }),
    uaeDrivingExperience: z.string().min(1, "Driving experience is required"),
    outsideUaeDrivingExperience: z.string(),
    hasActivePolicy: z.boolean(),
    currentPolicyType: z.string(),
    currentInsurer: z.string(),
    claimsLastYear: z.boolean().default(false),
    no_claims_certificate: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.hasActivePolicy && !data.currentPolicyType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Current policy type is required",
        path: ["currentPolicyType"],
      });
    }

    if (data.hasActivePolicy && !data.currentInsurer) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Current insurer is required",
        path: ["currentInsurer"],
      });
    }

    if (
      data.claimsLastYear === false &&
      (!data.no_claims_certificate || data.no_claims_certificate === "")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please provide a no claims certificate.",
        path: ["no_claims_certificate"],
      });
    }
  });

const getDobYear = (dob?: string) => {
  if (!dob) {
    return null;
  }

  const match = /^(\d{4})-\d{2}-\d{2}$/.exec(dob);
  return match ? match[1] : null;
};

export const createCarAdditionalInfoSchema = (driverDateOfBirth?: string) => {
  const nationalIdLabel = getNationalIdLabel();
  const nationalIdValidationMessage = getNationalIdValidationMessage();

  return z
    .object({
      estimatedAnnualMileage: z.string().trim().min(1, "Select estimated annual mileage"),

      chassisNumber: z
        .string()
        .trim()
        .min(1, "Enter chassis number")
        .length(17, "Chassis number must be exactly 17 characters")
        .regex(
          /^[A-HJ-NPR-Za-hj-npr-z0-9]+$/,
          "Chassis number must be alphanumeric and cannot contain I, O, or Q"
        )
        .transform((value) => value.toUpperCase()),

      emiratesId: z
        .string()
        .trim()
        .min(1, `Enter ${nationalIdLabel}`)
        .refine((value) => isValidNationalId(value), {
          message: nationalIdValidationMessage,
        }),

      carFinancedBy: z
        .string()
        .trim()
        .min(2, "Car financed by must be at least 2 characters")
        .max(100, "Car financed by must not exceed 100 characters")
        .regex(/^[A-Za-z0-9&.\-\s]+$/, "Only letters, numbers, spaces, &, -, and . are allowed")
        .optional()
        .or(z.literal("")),

      vehicleColor: z
        .string()
        .trim()
        .min(1, "Enter vehicle color")
        .regex(/^[A-Za-z ]+$/, "Vehicle color must contain only letters")
        .refine((value) => !/\s{2,}/.test(value), {
          message: "Vehicle color cannot contain consecutive spaces",
        }),

      tcfNumber: z
        .string()
        .trim()
        .min(5, "TCF No. must be at least 5 characters")
        .max(30, "TCF No. must not exceed 30 characters")
        .regex(/^[A-Za-z0-9-]+$/, "TCF No. must be alphanumeric"),

      registrationType: z.string().trim().min(1, "Select registration type"),

      plateCode: z
        .string()
        .trim()
        .min(1, "Enter plate code")
        .max(5, "Plate code must not exceed 5 characters"),

      plateNumber: z
        .string()
        .trim()
        .min(1, "Enter plate number")
        .max(10, "Plate number must not exceed 10 characters")
        .regex(/^[A-Za-z0-9\s]+$/, "Plate number can contain only letters, numbers, and spaces"),

      engineNumber: z
        .string()
        .trim()
        .min(5, "Engine number must be at least 5 characters")
        .max(30, "Engine number must not exceed 30 characters")
        .regex(/^[A-Za-z0-9-/]+$/, "Engine number must be alphanumeric"),

      authorizationAccepted: z.boolean().refine((value) => value === true, {
        message: "Please accept the authorization and terms",
      }),
    })
    .superRefine((data, ctx) => {
      const dobYear = getDobYear(driverDateOfBirth);

      const nationalIdYear = getNationalIdBirthYear(data.emiratesId);

      if (dobYear && nationalIdYear && dobYear !== nationalIdYear) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${nationalIdLabel} birth year must match date of birth`,
          path: ["emiratesId"],
        });
      }
    });
};

export const carAdditionalInfoSchema = createCarAdditionalInfoSchema();
